import { useQuery } from '@apollo/client';
import { useMemo } from 'react';

import type { Get_Ads_By_Line_Item_IdsQuery } from '../../../../../../../../../../apis/graphql';
import { useFetchCalculatedCreativeData } from '../../../../../../../../../../common/hooks/queries/useFetchCalculatedCreativeData';
import type { LineItemModel } from '../../../../../../../../TraffickingPage/modelConverters';
import type {
  AdEntity,
  MultipleEditDrawerAdsFormValues,
  MultipleEditDrawerLineItemsFormValues,
} from '../../../../../../types';
import { GET_ADS_BY_LINE_ITEM_IDS } from '../../../queries';
import type { UseCheckConflictsParams } from '../../../types';
import type { UseGetNormalizedTargetingResult } from '../../useGetNormalizedTargeting/useGetNormalizedTargeting';
import { checkStatusAdsConflicts } from './checkStatusAdsConflicts';
import { checkStatusLineItemConflicts } from './checkStatusLineItemConflicts';
import { buildAdTargetingTermValues } from './utils';

export type UseCheckStatusConflictsResult = {
  lineItemStatusConflicts?: {
    lineItemsWithoutActiveAd: Array<LineItemModel['id']>;
    lineItemsWithoutApproved: Array<LineItemModel['id']>;
  };
  lineItemStatusErrorLoading?: boolean;
  adsStatusConflicts?: Record<string, string[]> | null;
  isAdStatusErrorLoading?: boolean;
};

type Props = UseCheckConflictsParams & {
  values: MultipleEditDrawerAdsFormValues | MultipleEditDrawerLineItemsFormValues;
  existedTargeting?: UseGetNormalizedTargetingResult;
};

export const useCheckStatusConflicts = ({
  values,
  isLineItem,
  existedTargeting,
}: Props): UseCheckStatusConflictsResult => {
  const selectedEntitiesIds = useMemo(() => values.entities.filter((entity) => entity.isSelected).map(({ id }) => id), [
    values.entities,
  ]);
  const selectedEntities = useMemo(() => values.entities.filter(({ id }) => selectedEntitiesIds.includes(id)), [
    selectedEntitiesIds,
    values.entities,
  ]);
  const isStatusSectionSelected = useMemo(() => !!values.selectedSections.includes('Status'), [
    values.selectedSections,
  ]);

  const adTargetingTermValues = useMemo(
    () =>
      buildAdTargetingTermValues(
        selectedEntitiesIds,
        values.targetingTermValues,
        existedTargeting?.filterTargetingWithEntityId
      ),
    [selectedEntitiesIds, values.targetingTermValues, existedTargeting?.filterTargetingWithEntityId]
  );

  const { data: adsByLineItemsIds, loading } = useQuery<Get_Ads_By_Line_Item_IdsQuery>(GET_ADS_BY_LINE_ITEM_IDS, {
    variables: {
      lineItemIds: selectedEntitiesIds,
    },
    skip: !isLineItem || !isStatusSectionSelected,
  });

  const { creativeStatusMap, loading: isAdStatusErrorLoading } = useFetchCalculatedCreativeData({
    skip: isLineItem || !selectedEntitiesIds.length || !isStatusSectionSelected || !!existedTargeting?.loading,
    adTargetingTermValues,
  });

  return isLineItem
    ? {
        lineItemStatusConflicts: checkStatusLineItemConflicts({
          values: values as MultipleEditDrawerLineItemsFormValues,
          adsByLineItemsIds,
          isStatusSectionSelected,
        }),
        lineItemStatusErrorLoading: loading,
      }
    : {
        adsStatusConflicts: checkStatusAdsConflicts({
          isSectionSelected: isStatusSectionSelected,
          entities: selectedEntities as Array<AdEntity>,
          creativeStatusMap,
        }),
        isAdStatusErrorLoading: isAdStatusErrorLoading || existedTargeting?.loading,
      };
};
